import { defineComponent, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import _ from 'lodash';
import { withLoading, TwoDepthSelector } from '@hems/component';
import { HomeApplianceService } from '@hems/service';
import { Helper } from '@hems/util';
import { AxiosErrorException } from '@hems/util/src/exception/exception';
import { isEmptyString } from '@hems/util/src/helper/helper';
import { ApplianceItem } from '@/components';
import { APPLIANCE_TYPE } from '@/config/constants';
const SMARTTHINGS_API_BASE_URL = process.env.VUE_APP_SMARTTHINGS_API_URL;
export default defineComponent({
    name: 'ApplianceListContainer',
    components: {
        ApplianceItem,
        TwoDepthSelector,
    },
    props: {
        applianceList: {
            type: Object,
            required: true,
        },
    },
    emits: ['showDetail'],
    async setup(props, { emit }) {
        const { t } = useI18n();
        const homeApplianceService = new HomeApplianceService(window.axiosInstance.axios);
        const state = reactive({
            show: false,
            applianceList: [],
            filteredList: [],
            selectedAppliance: null,
            selectedRoom: null,
            locationSet: [],
        });
        const selectorOptionsState = reactive({
            locations: [],
        });
        watch(() => props.applianceList, async () => {
            state.show = false;
            await loadData();
            state.show = true;
        });
        watch(() => state.selectedRoom, async () => {
            state.show = false;
            showDetails(null);
            await loadData();
            state.selectedRoom
                ? (state.filteredList = state.applianceList.filter((item) => {
                    return item.room_id === state.selectedRoom;
                }))
                : (state.filteredList = state.applianceList);
            state.show = true;
        });
        function showDetails(item) {
            state.selectedAppliance = item;
            emit('showDetail', item);
        }
        // get image by device type
        const getImageName = (applianceType, isOn) => {
            let imageName = '';
            switch (applianceType) {
                case APPLIANCE_TYPE.HEAT_PUMP:
                    imageName = isOn ? 'ic_re_hydro_unit1_p2' : 'ic_re_hydro_unit_default';
                    break;
                case APPLIANCE_TYPE.AIR_CONDITIONER:
                    imageName = isOn ? 'ic_re_air_conditioner_p2' : 'ic_re_air_conditioner_default';
                    break;
                case APPLIANCE_TYPE.SMART_PLUG:
                    imageName = isOn ? 'ic_re_smart_plug_p2' : 'ic_re_smart_plug_default';
                    break;
                case APPLIANCE_TYPE.TV:
                    imageName = isOn ? 'ic_re_tv_p2' : 'ic_re_tv_default';
            }
            return imageName;
        };
        const getCommonDetailInfo = (comp) => {
            const currentValue = comp.temperatureMeasurement.temperature.value;
            const currentValueUnit = Helper.getTempertureUnit(comp.temperatureMeasurement.temperature.unit);
            const setValue = comp.thermostatCoolingSetpoint.coolingSetpoint.value;
            const setValueUnit = Helper.getTempertureUnit(comp.thermostatCoolingSetpoint.coolingSetpoint.unit);
            const minValue = comp['custom.thermostatSetpointControl'].minimumSetpoint.value;
            const minValueUnit = Helper.getTempertureUnit(comp['custom.thermostatSetpointControl'].minimumSetpoint.unit);
            const maxValue = comp['custom.thermostatSetpointControl'].maximumSetpoint.value;
            const maxValueUnit = Helper.getTempertureUnit(comp['custom.thermostatSetpointControl'].maximumSetpoint.unit);
            const mode = comp.airConditionerMode.airConditionerMode.value;
            const supportedModes = comp.airConditionerMode.supportedAcModes.value;
            const description = `${t('common.current')} ${Helper.formatNumber(currentValue, 1)} ${currentValueUnit} / ${t('common.appliance_set')} ${Helper.formatNumber(setValue, 1)} ${setValueUnit}`;
            return {
                description,
                currentValue,
                currentValueUnit,
                setValue,
                setValueUnit,
                minValue,
                minValueUnit,
                maxValue,
                maxValueUnit,
                mode,
                supportedModes,
            };
        };
        const getHeatPumpInfoByComponents = (comp, type) => {
            const isOn = comp.switch.switch.value === 'on';
            const isControlTemperature = true;
            const isControlMode = type === 'main';
            return {
                isOn,
                ...getCommonDetailInfo(comp),
                isControlTemperature,
                isControlMode,
            };
        };
        // get device details
        const getApplianceDetails = async (applianceItem) => {
            const authToken = await checkAuthStatus(applianceItem);
            if (isEmptyString(authToken))
                return Promise.resolve({});
            try {
                const axiosInstance = axios.create({
                    baseURL: SMARTTHINGS_API_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                });
                const homeApplianceService = new HomeApplianceService(axiosInstance);
                const res = await homeApplianceService.getApplianceStatus(applianceItem.device_id);
                const isOn = res.components.main?.switch?.switch?.value === 'on';
                const imageName = getImageName(applianceItem.profile_cd, isOn);
                const imagePath = imageName ? `homeAppliance2/${imageName}` : '';
                // get details by device type
                const details = applianceItem.profile_cd === APPLIANCE_TYPE.AIR_CONDITIONER
                    ? {
                        ...getCommonDetailInfo(res.components.main),
                        isControlTemperature: true,
                        isControlMode: true,
                        setTemperatureTitle: 'Set Temperature',
                    }
                    : applianceItem.profile_cd === APPLIANCE_TYPE.HEAT_PUMP
                        ? {
                            ...getHeatPumpInfoByComponents(res.components.main, 'main'),
                            setTemperatureTitle: 'Set Water Temperature',
                            indoor: {
                                ...getHeatPumpInfoByComponents(res.components.INDOOR, 'indoor'),
                                setTemperatureTitle: 'Set Water Temperature',
                            },
                        }
                        : { isControlTemerature: false, isControlMode: false };
                return { isOn, imageName: imagePath, ...details, ...applianceItem };
            }
            catch (e) {
                console.error(e);
                return Promise.resolve({});
            }
        };
        // get location and room
        const getApplianceLocationAndRoom = async (applianceItem) => {
            const authToken = await checkAuthStatus(applianceItem);
            if (isEmptyString(authToken))
                return Promise.resolve('');
            try {
                const axiosInstance = axios.create({
                    baseURL: SMARTTHINGS_API_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                });
                const homeApplianceService = new HomeApplianceService(axiosInstance);
                const res = await homeApplianceService.getApplianceLocations();
                const locations = await Promise.all(res.items.map((item) => {
                    return getRoomsByLocation(applianceItem, item.locationId, item.name);
                }));
                return locations;
            }
            catch (e) {
                console.error(e);
                return Promise.resolve('');
            }
        };
        const getRoomsByLocation = async (applianceItem, locationId, locationName) => {
            const authToken = await checkAuthStatus(applianceItem);
            if (isEmptyString(authToken))
                return Promise.resolve({});
            try {
                const axiosInstance = axios.create({
                    baseURL: SMARTTHINGS_API_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                });
                const homeApplianceService = new HomeApplianceService(axiosInstance);
                const res = await homeApplianceService.getApplianceRooms(locationId);
                const rooms = res.items.map((item) => ({ name: item.name, id: item.roomId }));
                state.locationSet.push({ location: locationName, locationId, rooms });
                return { location: locationName, locationId, rooms };
            }
            catch (e) {
                console.error(e);
                return Promise.resolve({});
            }
        };
        // get room by device
        const getApplianceRoom = async (applianceItem) => {
            const authToken = await checkAuthStatus(applianceItem);
            if (isEmptyString(authToken))
                return Promise.resolve('');
            try {
                const axiosInstance = axios.create({
                    baseURL: SMARTTHINGS_API_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                });
                const homeApplianceService = new HomeApplianceService(axiosInstance);
                const res = await homeApplianceService.getApplianceRoom(applianceItem.location_id, applianceItem.room_id);
                return { name: res.name, id: res.roomId };
            }
            catch (e) {
                console.error(e);
                return Promise.resolve('');
            }
        };
        // get name of device
        const getApplianceTitleAndChartCondition = async (applianceItem) => {
            const authToken = await checkAuthStatus(applianceItem);
            if (isEmptyString(authToken))
                return Promise.resolve('');
            try {
                const axiosInstance = axios.create({
                    baseURL: SMARTTHINGS_API_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                });
                const homeApplianceService = new HomeApplianceService(axiosInstance);
                const res = await homeApplianceService.getApplianceInfo(applianceItem.device_id);
                let isEnergyConsumption = true;
                // TV의 경우 vendor id 사용하여 2022년형 이상의 모델인지 판단(개별 장비 chart를 보여주는 조건)
                if (applianceItem.profile_cd === APPLIANCE_TYPE.TV) {
                    const vendorYear = parseInt(res.ocf?.vendorId.split('-').slice(-1)[0]);
                    if (isNaN(vendorYear) || vendorYear < 2022) {
                        isEnergyConsumption = false;
                    }
                }
                return { title: res.label, isEnergyConsumption };
            }
            catch (e) {
                console.error(e);
                return Promise.resolve('');
            }
        };
        // get on/offline status
        const getDeviceOnOffLine = async (applianceItem) => {
            const authToken = await checkAuthStatus(applianceItem);
            if (isEmptyString(authToken))
                return Promise.resolve(false);
            try {
                const axiosInstance = axios.create({
                    baseURL: SMARTTHINGS_API_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                });
                const homeApplianceService = new HomeApplianceService(axiosInstance);
                const res = await homeApplianceService.getApplianceHealth(applianceItem.device_id);
                return res.state === 'ONLINE';
            }
            catch (e) {
                console.error(e);
                return Promise.resolve(false);
            }
        };
        // auth token 유효성 체크
        const checkAuthStatus = async (applianceItem) => {
            try {
                const axiosInstance = axios.create({
                    baseURL: SMARTTHINGS_API_BASE_URL,
                    headers: {
                        Authorization: `Bearer ${applianceItem.auth_token}`,
                    },
                });
                const homeApplianceService = new HomeApplianceService(axiosInstance);
                await homeApplianceService.getApplianceInfo(applianceItem.device_id);
                return applianceItem.auth_token;
            }
            catch (e) {
                console.error(e);
                if (e instanceof AxiosErrorException) {
                    e.afterError({
                        401: async () => {
                            return await getRefreshToken(applianceItem.auth_token);
                        },
                    });
                }
                return '';
            }
        };
        // auth token 유효 기간이 1일 이므로 401 오류 발생 시 token refresh
        const getRefreshToken = async (authToken) => {
            try {
                const res = await homeApplianceService.refreshSmartThingsToken(authToken);
                return res.access_token;
            }
            catch (e) {
                console.error(e);
                return '';
            }
        };
        // smartthings 기기 리스트 항목 각각의 상세 데이터를 가져옴
        const getDeviceInfo = async () => {
            for (const item of props.applianceList) {
                await Promise.all([
                    getApplianceDetails(item),
                    getApplianceTitleAndChartCondition(item),
                    getApplianceRoom(item),
                    getApplianceLocationAndRoom(item),
                    getDeviceOnOffLine(item),
                ]).then(([applianceStatus, applianceNameAndChartCondition, applianceRoom, applianceLocationAndRoom, isOnline]) => {
                    if (!_.isEmpty(applianceStatus) && applianceNameAndChartCondition.title !== '') {
                        state.applianceList.push({
                            ...applianceStatus,
                            applianceName: applianceNameAndChartCondition.title,
                            room_id: applianceRoom.id,
                            room: applianceRoom.name,
                            location: applianceLocationAndRoom[0].location,
                            isOnline,
                            isEnergyConsumption: applianceNameAndChartCondition.isEnergyConsumption,
                        });
                        state.filteredList.push({
                            ...applianceStatus,
                            applianceName: applianceNameAndChartCondition.title,
                            room_id: applianceRoom.id,
                            room: applianceRoom.name,
                            location: applianceLocationAndRoom[0].location,
                            isOnline,
                            isEnergyConsumption: applianceNameAndChartCondition.isEnergyConsumption,
                        });
                    }
                });
            }
        };
        const loadData = withLoading(async () => {
            try {
                state.applianceList = [];
                state.filteredList = [];
                state.locationSet = [];
                await getDeviceInfo();
                // Location 선택 Option 생성
                selectorOptionsState.locations = [{ label: null, open: true, options: [{ text: 'Show All', value: null }] }];
                const locations = _.uniqWith(state.locationSet, _.isEqual);
                for (const location of locations) {
                    const roomsOptions = [];
                    for (const room of location.rooms) {
                        roomsOptions.push({ text: room.name, value: room.id });
                    }
                    selectorOptionsState.locations.push({ label: location.location, open: false, options: roomsOptions });
                }
            }
            catch (e) {
                console.error(e);
            }
        });
        await loadData();
        state.show = true;
        return {
            state,
            showDetails,
            selectorOptionsState,
        };
    },
});
